import React, { useState, useEffect } from 'react';
import './SaveChatModal.css';

interface SaveChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (title: string) => Promise<void>;
  initialTitle: string;
  messageCount: number;
  totalTokens: number;
  personaName: string | null;
  currentChatCount: number;
}

const SaveChatModal: React.FC<SaveChatModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialTitle,
  messageCount,
  totalTokens,
  personaName,
  currentChatCount
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle);
      setError(null);
      setIsSaving(false);
    }
  }, [isOpen, initialTitle]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSaving) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSaving, onClose]);

  const handleSave = async () => {
    const trimmedTitle = title.trim();

    if (!trimmedTitle) {
      setError('Please enter a chat title');
      return;
    }

    if (trimmedTitle.length > 255) {
      setError('Title must be 255 characters or less');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(trimmedTitle);
      // Success - parent will close modal
    } catch (err: any) {
      setError(err.message || 'Failed to save chat');
      setIsSaving(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSaving) {
      handleSave();
    }
  };

  if (!isOpen) return null;

  const isAtLimit = currentChatCount >= 10;

  return (
    <div className="save-chat-modal-overlay" onClick={onClose}>
      <div className="save-chat-modal" onClick={(e) => e.stopPropagation()}>
        <div className="save-chat-modal-header">
          <h2>💾 Save Conversation</h2>
          <button 
            className="save-chat-modal-close" 
            onClick={onClose}
            disabled={isSaving}
          >
            ✕
          </button>
        </div>

        <div className="save-chat-modal-body">
          <div className="save-chat-input-group">
            <label htmlFor="chat-title">Chat Name:</label>
            <input
              id="chat-title"
              type="text"
              className="save-chat-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter a name for this conversation"
              maxLength={255}
              disabled={isSaving}
              autoFocus
            />
            <div className="save-chat-char-count">
              {title.length}/255 characters
            </div>
          </div>

          <div className="save-chat-preview">
            <h3>Preview:</h3>
            <div className="save-chat-preview-items">
              <div className="save-chat-preview-item">
                <span className="preview-icon">💬</span>
                <span className="preview-label">{messageCount} messages</span>
              </div>
              {personaName && (
                <div className="save-chat-preview-item">
                  <span className="preview-icon">🎭</span>
                  <span className="preview-label">Persona: {personaName}</span>
                </div>
              )}
            </div>
          </div>

          <div className="save-chat-info">
            <div className="save-chat-count-badge">
              Saved Chats: <strong>{currentChatCount}/10</strong>
            </div>
            {isAtLimit && (
              <div className="save-chat-warning">
                ⚠️ You've reached the 10-chat limit. Saving will replace your oldest chat.
              </div>
            )}
            {!isAtLimit && currentChatCount >= 8 && (
              <div className="save-chat-info-text">
                💡 You can save up to 10 conversations. ({10 - currentChatCount} remaining)
              </div>
            )}
          </div>

          {error && (
            <div className="save-chat-error">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{error}</span>
            </div>
          )}
        </div>

        <div className="save-chat-modal-footer">
          <button
            className="save-chat-btn-cancel"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="save-chat-btn-save"
            onClick={handleSave}
            disabled={isSaving || !title.trim()}
          >
            {isSaving ? (
              <>
                <span className="save-chat-spinner"></span>
                Saving...
              </>
            ) : (
              <>💾 Save Chat</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveChatModal;
















